import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { MultiSelect } from '../ui/multi-select';
import { CheckToggle } from './CheckToggle';
import { NotificationCardHeader } from './NotificationCardHeader';
import { NotificationCardError } from './NotificationCardError';
import { NotificationHourSelect } from './NotificationHourSelect';
import { NotificationResetFooter } from './NotificationResetFooter';
import { PlaceholderBadges } from './PlaceholderBadges';
import { PlaceholdersInfoTooltip } from './PlaceholdersInfoTooltip';
import { TemplatePreview } from './TemplatePreview';
import { insertPlaceholderIntoText } from './useInsertPlaceholder';

export const MAX_MILESTONES = 10;
export const MAX_MILESTONE_MESSAGE_LENGTH = 1000;

export interface AnniversaryMilestoneDraft {
  year: number;
  message: string;
  enabled: boolean;
}

export interface AnniversarySettingsDraft {
  channel: string;
  notificationHour: number;
  sendDmToEmployee: boolean;
  tagEmployee: boolean;
  tagHere: boolean;
  milestones: AnniversaryMilestoneDraft[];
}

interface WorkAnniversaryCardProps {
  /** Anniversary configuration draft (org-wide settings + milestones). */
  draft: AnniversarySettingsDraft;
  /** Base message body, used as the default message for newly added milestones. */
  body: string;
  /**
   * The WORK_ANNIVERSARY template's `enabled` flag acts as a master switch. When
   * off, the per-milestone toggles are disabled; their stored values are kept so
   * they are restored when the master is re-enabled.
   */
  enabled: boolean;
  allowedParams: string[];
  /** Per-milestone validation errors, keyed by milestone year. */
  milestoneErrors?: Record<number, string>;
  isResetting?: boolean;
  onEnabledChange: (value: boolean) => void;
  onChange: (patch: Partial<AnniversarySettingsDraft>) => void;
  onReset: () => void;
}

/**
 * Single collapsible "Work Anniversary" card unifying the notification template
 * body/enabled controls with the organization-wide anniversary settings
 * (channel, hour, DM/tag switches) and the per-milestone editor (message +
 * enable switch). Presentational — all state is owned by the containing page.
 */
export const WorkAnniversaryCard: React.FC<WorkAnniversaryCardProps> = ({
  draft,
  body,
  enabled,
  allowedParams,
  milestoneErrors = {},
  isResetting,
  onEnabledChange,
  onChange,
  onReset,
}) => {
  const { t } = useTranslation('admin');
  const [expanded, setExpanded] = useState(false);
  const [collapsedMilestones, setCollapsedMilestones] = useState<Record<number, boolean>>({});
  const milestoneRefs = useRef<Record<number, HTMLTextAreaElement | null>>({});

  const sortedMilestones = [...draft.milestones].sort((a, b) => a.year - b.year);
  const milestoneYearsAsStrings = sortedMilestones.map((milestone) => milestone.year.toString());
  const atMaxMilestones = draft.milestones.length >= MAX_MILESTONES;

  const toggleMilestone = (year: number) => {
    setCollapsedMilestones((prev) => ({ ...prev, [year]: !prev[year] }));
  };

  const insertMilestonePlaceholder = (year: number, param: string) => {
    const milestone = draft.milestones.find((m) => m.year === year);
    insertPlaceholderIntoText({
      param,
      text: milestone?.message ?? '',
      onChange: (message) => updateMilestone(year, { message }),
      el: milestoneRefs.current[year],
      maxLength: MAX_MILESTONE_MESSAGE_LENGTH,
    });
  };

  const handleMilestoneYearsChange = (values: string[]) => {
    const years = Array.from(
      new Set(
        values
          .map((value) => parseInt(value, 10))
          .filter((year) => Number.isInteger(year) && year > 0)
      )
    )
      .sort((a, b) => a - b)
      .slice(0, MAX_MILESTONES);

    const byYear = new Map(draft.milestones.map((milestone) => [milestone.year, milestone]));
    const milestones = years.map(
      (year) => byYear.get(year) ?? { year, message: body, enabled: true }
    );
    onChange({ milestones });
  };

  const updateMilestone = (year: number, patch: Partial<AnniversaryMilestoneDraft>) => {
    onChange({
      milestones: draft.milestones.map((milestone) =>
        milestone.year === year ? { ...milestone, ...patch } : milestone
      ),
    });
  };

  type ToggleKey = 'sendDmToEmployee' | 'tagEmployee' | 'tagHere';
  const toggles: Array<{ key: ToggleKey; testId: string }> = [
    { key: 'sendDmToEmployee', testId: 'send-dm-to-employee' },
    { key: 'tagEmployee', testId: 'tag-employee' },
    { key: 'tagHere', testId: 'tag-here' },
  ];

  return (
    <Card data-testid="work-anniversary-card">
      <NotificationCardHeader
        title={t('anniversarySettings.title')}
        expanded={expanded}
        onToggleExpanded={() => setExpanded((prev) => !prev)}
        enabled={enabled}
        onEnabledChange={onEnabledChange}
        testIdPrefix="work-anniversary"
      />

      {expanded && (
        <>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="anniversary-channel">{t('anniversarySettings.channelLabel')}</Label>
              <Input
                id="anniversary-channel"
                value={draft.channel}
                onChange={(e) => onChange({ channel: e.target.value })}
                placeholder={t('anniversarySettings.channelPlaceholder')}
                data-testid="anniversary-channel"
              />
              <p className="text-xs text-muted-foreground">
                {t('anniversarySettings.channelHint')}
              </p>
            </div>

            <NotificationHourSelect
              value={draft.notificationHour}
              onChange={(notificationHour) => onChange({ notificationHour })}
              id="anniversary-notification-hour"
              testId="anniversary-notification-hour"
              label={t('anniversarySettings.notificationHourLabel')}
              hint={t('anniversarySettings.notificationHourHint')}
            />

            <div className="flex flex-col gap-2">
              {toggles.map(({ key, testId }) => (
                <div
                  key={key}
                  className="flex items-start gap-2"
                  data-testid={`anniversary-${testId}-label`}
                >
                  <CheckToggle
                    checked={draft[key] as boolean}
                    onCheckedChange={(checked) => onChange({ [key]: checked })}
                    testId={`anniversary-${testId}`}
                  />
                  <span className="text-sm">
                    <span className="font-medium">{t(`anniversarySettings.${key}.label`)}</span>
                    <span className="block text-xs text-muted-foreground">
                      {t(`anniversarySettings.${key}.hint`)}
                    </span>
                  </span>
                </div>
              ))}
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Label>{t('anniversarySettings.milestonesLabel')}</Label>
                <PlaceholdersInfoTooltip
                  allowedParams={allowedParams}
                  testIdPrefix="anniversary"
                  labelKey="anniversarySettings.placeholdersLabel"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {t('anniversarySettings.milestonesHint', { max: MAX_MILESTONES })}
              </p>
              <MultiSelect
                value={milestoneYearsAsStrings}
                onChange={handleMilestoneYearsChange}
                placeholder={t('anniversarySettings.milestoneYearsPlaceholder')}
              />
              {atMaxMilestones && (
                <p className="text-xs text-amber-700" data-testid="anniversary-milestones-at-max">
                  {t('anniversarySettings.milestonesAtMax', { max: MAX_MILESTONES })}
                </p>
              )}
              {!enabled && (
                <p
                  className="text-xs text-amber-700"
                  data-testid="anniversary-milestones-master-off"
                >
                  {t('anniversarySettings.milestonesMasterDisabled')}
                </p>
              )}

              <div className="space-y-4">
                {sortedMilestones.map((milestone) => {
                  const milestoneError = milestoneErrors[milestone.year];
                  const isCollapsed = collapsedMilestones[milestone.year] ?? false;
                  return (
                    <div
                      key={milestone.year}
                      className="rounded-md border border-prosigliere-grey p-3 space-y-2"
                      data-testid={`anniversary-milestone-${milestone.year}`}
                    >
                      <div className="flex items-center justify-between gap-4">
                        <button
                          type="button"
                          className="flex items-center gap-2 text-left text-sm font-medium"
                          onClick={() => toggleMilestone(milestone.year)}
                          aria-expanded={!isCollapsed}
                          data-testid={`anniversary-milestone-${milestone.year}-collapse-toggle`}
                        >
                          {isCollapsed ? (
                            <ChevronRight className="h-4 w-4 shrink-0" />
                          ) : (
                            <ChevronDown className="h-4 w-4 shrink-0" />
                          )}
                          {t('anniversarySettings.milestoneYearHeading', { year: milestone.year })}
                        </button>
                        <label
                          className="flex items-center gap-2 text-sm text-muted-foreground whitespace-nowrap"
                          data-testid={`anniversary-milestone-${milestone.year}-enabled-label`}
                        >
                          <Switch
                            checked={milestone.enabled}
                            disabled={!enabled}
                            onCheckedChange={(checked) =>
                              updateMilestone(milestone.year, { enabled: checked })
                            }
                            data-testid={`anniversary-milestone-${milestone.year}-enabled`}
                          />
                          {milestone.enabled
                            ? t('anniversarySettings.milestoneEnabled')
                            : t('anniversarySettings.milestoneDisabled')}
                        </label>
                      </div>
                      {!isCollapsed && (
                        <>
                          <PlaceholderBadges
                            allowedParams={allowedParams}
                            onInsert={(param) =>
                              insertMilestonePlaceholder(milestone.year, param)
                            }
                            testIdPrefix={`anniversary-milestone-${milestone.year}`}
                          />
                          <Textarea
                            ref={(el) => {
                              milestoneRefs.current[milestone.year] = el;
                            }}
                            value={milestone.message}
                            onChange={(e) =>
                              updateMilestone(milestone.year, {
                                message: e.target.value.slice(0, MAX_MILESTONE_MESSAGE_LENGTH),
                              })
                            }
                            rows={3}
                            maxLength={MAX_MILESTONE_MESSAGE_LENGTH}
                            className="font-mono"
                            placeholder={t('anniversarySettings.milestoneMessagePlaceholder')}
                            data-testid={`anniversary-milestone-${milestone.year}-message`}
                          />
                          <p
                            className="text-right text-xs text-muted-foreground"
                            data-testid={`anniversary-milestone-${milestone.year}-char-count`}
                          >
                            {milestone.message.length}/{MAX_MILESTONE_MESSAGE_LENGTH}
                          </p>
                          <div className="space-y-1">
                            <p className="text-xs font-medium text-muted-foreground">
                              {t('notificationTemplates.previewLabel')}
                            </p>
                            <TemplatePreview
                              body={milestone.message}
                              previewParams={{ years: milestone.year.toString() }}
                              testId={`anniversary-milestone-${milestone.year}-preview`}
                            />
                          </div>
                          {milestoneError && (
                            <NotificationCardError
                              message={milestoneError}
                              testId={`anniversary-milestone-${milestone.year}-error`}
                            />
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
          <NotificationResetFooter
            onReset={onReset}
            isResetting={isResetting}
            testId="work-anniversary-reset"
          />
        </>
      )}
    </Card>
  );
};
